import { useState, useEffect } from "react";
import { apiFetch, BASE_URL } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card,
  PageHeader,
  GhostBtn,
  GoldBtn,
  OutlineBtn,
  SectionTitle,
  ConfirmModal,
  EmptyState,
  Field,
  Input,
} from "../components/common/Ui";

const emptyForm = { id: null, name: "", subtitle: "", image: null, oldPrice: "", newPrice: "", productHref: "" };

// Searchable picker for real Daily Planner products (the only DB-backed
// collection). For any other collection, admins type a link manually below.
const ProductLinkPicker = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${BASE_URL}/public/products.php?category=daily-planner`)
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setResults(json.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = results.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between border rounded-xl px-3 py-2.5 text-[12px]"
        style={{ borderColor: T.border, color: T.textSec }}
      >
        Search Daily Planner products…
        <span>▾</span>
      </button>

      {open && (
        <div
          className="absolute z-10 mt-1 w-full rounded-xl overflow-hidden shadow-lg"
          style={{ background: T.card, border: `1px solid ${T.border}` }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products…"
            className="w-full px-3 py-2 text-[12px] outline-none border-b"
            style={{ borderColor: T.border }}
          />
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <p className="text-[11px] p-3" style={{ color: T.textSec }}>Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-[11px] p-3" style={{ color: T.textSec }}>No products found.</p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.dbId}
                  type="button"
                  onClick={() => {
                    onSelect(`/dailyplanners/${p.id}`);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[12px] hover:bg-black/5"
                >
                  {p.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HomeNotebook = () => {
  // ── Single side banner image ──
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerRemoving, setBannerRemoving] = useState(false);

 const loadBanner = () => {
  setBannerLoading(true);
  apiFetch("/home/notebooks.php?type=banner&admin=true")
    .then((res) => setBannerImage(res.data?.image_url || null))
    .catch(() => {})
    .finally(() => setBannerLoading(false));
};

  useEffect(loadBanner, []);

  const handleBannerPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append("type", "banner");
      fd.append("image", file);
      const res = await apiFetch("/home/notebooks.php?type=banner", { method: "POST", body: fd });
      setBannerImage(res.data?.image_url || URL.createObjectURL(file));
    } catch (err) {
      alert(err.message || "Failed to upload banner.");
    } finally {
      setBannerUploading(false);
      e.target.value = "";
    }
  };

  const handleBannerRemove = async () => {
    setBannerRemoving(true);
    try {
      await apiFetch("/home/notebooks.php?type=banner", { method: "DELETE" });
      setBannerImage(null);
    } catch (err) {
      alert(err.message || "Failed to remove banner.");
    } finally {
      setBannerRemoving(false);
    }
  };

  // ── Product cards ──
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEditing = Boolean(form.id);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const mapRow = (r) => ({
    id: r.id,
    name: r.name,
    subtitle: r.subtitle || "",
    image: r.image_url,
    oldPrice: r.old_price ?? "",
    newPrice: r.new_price ?? "",
    productHref: r.product_href || "",
  });

 const loadProducts = () => {
  setLoading(true);
  setLoadError("");
  apiFetch("/home/notebooks.php?admin=true")
    .then((res) => setProducts((res.data || []).map(mapRow)))
    .catch((err) => setLoadError(err.message || "Failed to load products."))
    .finally(() => setLoading(false));
};

  useEffect(loadProducts, []);

  const openAdd = () => {
    setForm(emptyForm);
    setNewFile(null);
    setFormOpen(true);
  };

  const openEdit = (product) => {
    setForm(product);
    setNewFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm);
    setNewFile(null);
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewFile(file);
    setForm((f) => ({ ...f, image: URL.createObjectURL(file) }));
    e.target.value = "";
  };

  // const handleSave = async () => {
  //   if (!form.name.trim()) return;
  //   if (!form.productHref.trim()) {
  //     alert("Please link this card to a product first — search and select one above.");
  //     return;
  //   }
  //   setSaving(true);
  //   try {
  //     const fd = new FormData();
  //     if (form.id) fd.append("id", form.id);
  //     fd.append("name", form.name);
  //     fd.append("subtitle", form.subtitle || "");
  //     fd.append("old_price", form.oldPrice || "");
  //     fd.append("new_price", form.newPrice || "");
  //     fd.append("product_href", form.productHref || "");
  //     if (newFile) fd.append("image", newFile);

  //     await apiFetch("/home/notebooks.php", { method: "POST", body: fd });
  //     closeForm();
  //     loadProducts();
  //   } catch (err) {
  //     alert(err.message || "Failed to save product.");
  //   } finally {
  //     setSaving(false);
  //   }
  // };


  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if (form.id) fd.append("id", form.id);
      fd.append("name", form.name);
      fd.append("subtitle", form.subtitle || "");
      fd.append("old_price", form.oldPrice || "");
      fd.append("new_price", form.newPrice || "");
      fd.append("product_href", form.productHref || "");
      if (newFile) fd.append("image", newFile);

      await apiFetch("/home/notebooks.php", { method: "POST", body: fd });
      closeForm();
      loadProducts();
    } catch (err) {
      alert(err.message || "Failed to save product.");
    } finally {
      setSaving(false);
    }
  };
  const confirmDelete = async () => {
    try {
      await apiFetch(`/home/notebooks.php?id=${deleteTarget}`, { method: "DELETE" });
      setDeleteTarget(null);
      loadProducts();
    } catch (err) {
      alert(err.message || "Failed to delete product.");
      setDeleteTarget(null);
    }
  };

  return (
    <div className="p-4 sm:p-6" style={{ background: T.bg, minHeight: "100vh" }}>
      <PageHeader
        title="Home Notebook"
        subtitle='Manage the "Notebooks & journals" section on the homepage'
      >
        <GoldBtn onClick={openAdd}>+ Add Product</GoldBtn>
      </PageHeader>

      {/* ── Single side banner image ── */}
      <Card className="p-4 sm:p-6 mb-5 sm:mb-6">
        <SectionTitle>Side Banner Image</SectionTitle>
        <p className="text-[12px] mb-4" style={{ color: T.textSec }}>
          This single image shows in the tall side panel next to the product list.
        </p>
        {bannerLoading ? (
          <p className="text-[12px]" style={{ color: T.textSec }}>Loading…</p>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <div
              className="w-28 h-36 sm:w-32 sm:h-44 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
              style={{ background: T.bgSoft, border: `1.5px solid ${T.border}` }}
            >
              {bannerImage ? (
                <img src={bannerImage} alt="Side banner" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[10px] text-center px-2" style={{ color: T.textSec }}>No image</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label
                className="flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-4 text-[12px] font-semibold cursor-pointer transition-all"
                style={{ border: `1.5px dashed ${T.gold}`, color: T.gold, opacity: bannerUploading ? 0.6 : 1 }}
              >
                {bannerUploading ? "Uploading…" : "Choose from gallery"}
                <input type="file" accept="image/*" className="hidden" onChange={handleBannerPick} disabled={bannerUploading} />
              </label>
              {bannerImage && (
                <GhostBtn onClick={handleBannerRemove}>{bannerRemoving ? "Removing…" : "Remove"}</GhostBtn>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* ── Product cards ── */}
      <Card className="p-4 sm:p-6">
        <SectionTitle>Product Cards</SectionTitle>

        {loading ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.textSec }}>Loading…</p>
        ) : loadError ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.danger }}>{loadError}</p>
        ) : products.length === 0 ? (
          <EmptyState
            icon="📔"
            title="No products yet"
            sub="Add your first product to show it in this section."
            action="Add Product"
            onAction={openAdd}
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {products.map((p) => (
              <div key={p.id} className="rounded-2xl overflow-hidden flex flex-col" style={{ border: `1px solid ${T.border}` }}>
                <div className="aspect-[4/5] flex items-center justify-center" style={{ background: T.bgSoft }}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[11px]" style={{ color: T.textSec }}>No photo</span>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <p className="text-[13px] font-bold truncate" style={{ color: T.textPri }}>{p.name || "Untitled"}</p>
                  {p.subtitle && <p className="text-[11px]" style={{ color: T.textSec }}>{p.subtitle}</p>}
                  <div className="flex items-center gap-1.5 mt-1">
                    {p.newPrice !== "" && <span className="text-[13px] font-bold" style={{ color: T.textPri }}>Rs. {p.newPrice}</span>}
                    {p.oldPrice !== "" && (
                      <span className="text-[11px] font-semibold px-1 rounded-sm line-through" style={{ background: "#FCE954", color: T.textPri }}>
                        Rs. {p.oldPrice}
                      </span>
                    )}
                  </div>
                  {p.productHref && (
                    <p className="text-[10px] truncate mt-0.5" style={{ color: T.gold }}>
                      → {p.productHref}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 p-2.5" style={{ borderTop: `1px solid ${T.border}` }}>
                  <GhostBtn onClick={() => openEdit(p)}>Edit</GhostBtn>
                  <GhostBtn onClick={() => setDeleteTarget(p.id)}>Delete</GhostBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Add / Edit form modal ── */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={closeForm}>
          <div className="rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" style={{ background: T.card }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-[15px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>{isEditing ? "Edit Product" : "Add Product"}</h3>
              <button onClick={closeForm} className="text-[13px] font-medium" style={{ color: T.textSec }}>✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <Field label="Photo">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
                    {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <span className="text-[9px] text-center px-1" style={{ color: T.textSec }}>No photo</span>}
                  </div>
                  <label className="flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-4 text-[12px] font-semibold cursor-pointer transition-all flex-1" style={{ border: `1.5px dashed ${T.gold}`, color: T.gold }}>
                    Choose from gallery
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoPick} />
                  </label>
                </div>
              </Field>

              <Field label="Name" required>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Every day" />
              </Field>

              <Field label="Subtitle">
                <Input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="e.g. Medium Diary" />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Old Price">
                  <Input type="number" value={form.oldPrice} onChange={(e) => setForm((f) => ({ ...f, oldPrice: e.target.value }))} placeholder="799" />
                </Field>
                <Field label="New Price">
                  <Input type="number" value={form.newPrice} onChange={(e) => setForm((f) => ({ ...f, newPrice: e.target.value }))} placeholder="399" />
                </Field>
              </div>

             <Field label="Link to Product (optional)">
  <div className="space-y-2">
    <ProductLinkPicker onSelect={(href) => setForm((f) => ({ ...f, productHref: href }))} />
    <Input
      value={form.productHref}
      onChange={(e) => setForm((f) => ({ ...f, productHref: e.target.value }))}
      placeholder="/dailyplanners/some-slug — or type any custom link"
    />
    <p className="text-[10px] leading-relaxed" style={{ color: T.textSec }}>
      Search above to link a real Daily Planner product, or paste any link manually. Leave blank and it'll get its own automatic product page instead.
    </p>
  </div>
</Field>
            </div>

            <div className="flex items-center gap-2.5 p-5 flex-shrink-0" style={{ borderTop: `1px solid ${T.border}` }}>
              <OutlineBtn onClick={closeForm}>Cancel</OutlineBtn>
              <GoldBtn onClick={handleSave}>{saving ? "Saving…" : isEditing ? "Save Changes" : "Add Product"}</GoldBtn>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete product?"
          message="This product will be removed from the section."
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default HomeNotebook;